/**
 * Países: código ISO 3166-1 alpha-2 y prefijo telefónico.
 *
 * Se usa en dos lugares distintos y NO se mezclan: la nacionalidad (dato de
 * la persona, lo elige ella) y el prefijo del WhatsApp (dato del teléfono).
 * Alguien nacido en Nicaragua puede tener número tico y al revés, así que
 * elegir uno jamás cambia el otro.
 *
 * El nombre del país NO está acá: lo da `Intl.DisplayNames` en el idioma
 * actual, así los nueve idiomas quedan cubiertos sin mantener nueve listas
 * de doscientos nombres que se desactualizan por separado.
 */
import { flagEmoji } from './flag';

const DIAL_CODES = `
AD:376 AE:971 AF:93 AG:1268 AI:1264 AL:355 AM:374 AO:244 AR:54 AS:1684 AT:43
AU:61 AW:297 AX:358 AZ:994 BA:387 BB:1246 BD:880 BE:32 BF:226 BG:359 BH:973
BI:257 BJ:229 BL:590 BM:1441 BN:673 BO:591 BQ:599 BR:55 BS:1242 BT:975 BW:267
BY:375 BZ:501 CA:1 CD:243 CF:236 CG:242 CH:41 CI:225 CK:682 CL:56 CM:237 CN:86
CO:57 CR:506 CU:53 CV:238 CW:599 CY:357 CZ:420 DE:49 DJ:253 DK:45 DM:1767
DO:1809 DZ:213 EC:593 EE:372 EG:20 ER:291 ES:34 ET:251 FI:358 FJ:679 FK:500
FM:691 FO:298 FR:33 GA:241 GB:44 GD:1473 GE:995 GF:594 GG:44 GH:233 GI:350
GL:299 GM:220 GN:224 GP:590 GQ:240 GR:30 GT:502 GU:1671 GW:245 GY:592 HK:852
HN:504 HR:385 HT:509 HU:36 ID:62 IE:353 IL:972 IM:44 IN:91 IQ:964 IR:98 IS:354
IT:39 JE:44 JM:1876 JO:962 JP:81 KE:254 KG:996 KH:855 KI:686 KM:269 KN:1869
KP:850 KR:82 KW:965 KY:1345 KZ:7 LA:856 LB:961 LC:1758 LI:423 LK:94 LR:231
LS:266 LT:370 LU:352 LV:371 LY:218 MA:212 MC:377 MD:373 ME:382 MF:590 MG:261
MH:692 MK:389 ML:223 MM:95 MN:976 MO:853 MP:1670 MQ:596 MR:222 MS:1664 MT:356
MU:230 MV:960 MW:265 MX:52 MY:60 MZ:258 NA:264 NC:687 NE:227 NG:234 NI:505
NL:31 NO:47 NP:977 NR:674 NU:683 NZ:64 OM:968 PA:507 PE:51 PF:689 PG:675 PH:63
PK:92 PL:48 PM:508 PR:1787 PS:970 PT:351 PW:680 PY:595 QA:974 RE:262 RO:40
RS:381 RU:7 RW:250 SA:966 SB:677 SC:248 SD:249 SE:46 SG:65 SH:290 SI:386 SK:421
SL:232 SM:378 SN:221 SO:252 SR:597 SS:211 ST:239 SV:503 SX:1721 SY:963 SZ:268
TC:1649 TD:235 TG:228 TH:66 TJ:992 TL:670 TM:993 TN:216 TO:676 TR:90 TT:1868
TV:688 TW:886 TZ:255 UA:380 UG:256 US:1 UY:598 UZ:998 VA:39 VC:1784 VE:58
VG:1284 VI:1340 VN:84 VU:678 WF:681 WS:685 YE:967 YT:262 ZA:27 ZM:260 ZW:263
`;

/**
 * Un número nacional de ejemplo, para mostrarle a la persona qué se espera
 * de ella al cambiar de país. Solo para los países con presencia real en el
 * producto; para el resto se muestra una indicación genérica.
 */
const EXAMPLES: Readonly<Record<string, string>> = {
  AR: '11 2345 6789', BO: '7123 4567', BR: '11 91234 5678', CA: '506 234 5678',
  CL: '9 1234 5678', CN: '131 2345 6789', CO: '300 123 4567', CR: '8888 7777',
  CU: '5 123 4567', DE: '1512 3456789', DO: '809 123 4567', EC: '99 123 4567',
  ES: '612 345 678', FR: '6 12 34 56 78', GB: '7400 123456', GT: '5123 4567',
  HN: '9123 4567', IT: '312 345 6789', JP: '90 1234 5678', KR: '10 1234 5678',
  MX: '55 1234 5678', NI: '8888 7777', PA: '6123 4567', PE: '912 345 678',
  PT: '912 345 678', PY: '981 123456', SV: '7123 4567', US: '202 555 0147',
  UY: '94 123 456', VE: '412 123 4567',
};

export interface Country {
  /** ISO 3166-1 alpha-2. */
  code: string;
  /** Prefijo telefónico, sin el "+". */
  dial: string;
  /** Emoji de bandera; siempre se muestra junto al nombre, nunca solo. */
  flag: string;
  /** Número nacional de ejemplo, si lo tenemos. */
  example: string | undefined;
}

export const COUNTRIES: readonly Country[] = DIAL_CODES.trim()
  .split(/\s+/)
  .map((pair) => {
    const [code = '', dial = ''] = pair.split(':');
    return { code, dial, flag: flagEmoji(code), example: EXAMPLES[code] };
  });

const BY_CODE: ReadonlyMap<string, Country> = new Map(COUNTRIES.map((c) => [c.code, c] as const));

export function findCountry(code: string): Country | undefined {
  return BY_CODE.get(code);
}

/** El país que arranca elegido en el prefijo de WhatsApp. */
export const DEFAULT_PHONE_COUNTRY = 'CR';

const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function regionNames(locale: string): Intl.DisplayNames | null {
  const cached = displayNamesCache.get(locale);
  if (cached !== undefined) return cached;
  let value: Intl.DisplayNames | null = null;
  try {
    value = new Intl.DisplayNames([locale, 'es'], { type: 'region' });
  } catch {
    // Un runtime sin datos de ICU para ese idioma: caemos al código.
    value = null;
  }
  displayNamesCache.set(locale, value);
  return value;
}

/** Nombre del país en el idioma pedido; el código si el runtime no lo sabe. */
export function countryName(code: string, locale: string): string {
  return regionNames(locale)?.of(code) ?? code;
}

/** La lista completa ordenada por nombre en el idioma pedido. */
export function countriesSortedFor(locale: string): readonly Country[] {
  const collator = new Intl.Collator(locale);
  return [...COUNTRIES].sort((a, b) =>
    collator.compare(countryName(a.code, locale), countryName(b.code, locale)),
  );
}
